import type { ApolloError } from "@apollo/client";
import { useNavigate } from "@tanstack/react-router";
import { type Row as TanstackRow, flexRender } from "@tanstack/react-table";
import { Clock } from "lucide-react";
import {
  type PropsWithChildren,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import type { InvoiceData } from "@/apis/gql/types";
import { TableLayout } from "@/components/layouts/table-layout";
import { PrefetchPdfOnHover } from "@/components/shared/pdf-tools/components/prefetch-pdf-on-hover";
import { SkeletonTable } from "@/components/shared/tables/skeleton-table";
import {
  Table,
  TableBody as TableBodyBase,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePermissions } from "@/hooks/usePermissions";
import { usePreview } from "@/hooks/usePreview";
import {
  InvoiceProcessingLoadingSteps,
  type InvoiceProcessingStatus,
  InvoiceStatus,
} from "@/lib/constants/invoiceStatus";
import { cn } from "@/lib/utils";
import { useInvoiceBrowser } from "@/providers/invoice-browser";
import { useInvoicesContext } from "@/providers/invoices";
import { useTenant } from "@/providers/session";

import { NoRecordsEmpty } from "@/components/shared/data-table/empty-message";
import { ServerSideDataTablePagination } from "@/components/shared/tables/server-side-table-pagination";
import { ErrorMessage } from "@/components/ui/error";
import { useInvoiceTableInstanceContext } from "./table-instance-provider";
import { hasFullConfidence } from "./utils/utils";

type Props = PropsWithChildren<{
  totalItems: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  error: ApolloError | undefined;
  emptyStateComponent?: React.ReactNode;
}>;

export const ANIMATION_DURATION = 600;

export const InvoicesDataTable = ({
  children,
  totalItems,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  error,
  emptyStateComponent,
}: Props) => {
  const { t } = useTranslation();
  const tableRef = useRef(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { loading } = useInvoicesContext();
  const { companySlug, isOcrReviewRequired } = useTenant();
  const { isSuperAdmin } = usePermissions();
  const { setInvoiceBrowsingQueue } = useInvoiceBrowser();
  const navigate = useNavigate();

  const isRowEditable = (ocrReviewed: boolean, status: InvoiceStatus) => {
    if (status === InvoiceStatus.ERROR) {
      return true;
    }

    if (isOcrReviewRequired) {
      return ocrReviewed || isSuperAdmin;
    }

    return true;
  };

  const { table, clickableColumns } = useInvoiceTableInstanceContext();

  const handleRowClick = useCallback(
    (event: any, rowData: any) => {
      event.stopPropagation();
      if (isRowEditable(rowData.invoice.ocrReviewed, rowData.invoice.status)) {
        const ids = table
          .getSortedRowModel()
          .rows.map((row) => row.original.invoice.invoiceId);

        setInvoiceBrowsingQueue(ids as string[], rowData?.invoice.invoiceId);

        if (rowData.invoice.invoiceId) {
          if (rowData.invoice.splitPdf) {
            navigate({
              to: "/$company/buchhaltung/rechnung/$id/seiten-bearbeiten",
              params: { company: companySlug, id: rowData.invoice.invoiceId },
            });
          } else {
            navigate({
              to: "/$company/buchhaltung/rechnung/$id",
              params: { company: companySlug, id: rowData.invoice.invoiceId },
            });
          }
        }
      }
    },
    [
      table,
      isOcrReviewRequired,
      isSuperAdmin,
      setInvoiceBrowsingQueue,
      navigate,
      companySlug,
    ],
  );

  const invoiceRowClass = (invoice: InvoiceData, selected: boolean) => {
    const ocrReviewed = invoice.ocrReviewed;

    const invoiceHasFullConfidence = hasFullConfidence(invoice);

    if (isOcrReviewRequired && !ocrReviewed && isSuperAdmin) {
      return "bg-orange-100";
    } else if (isOcrReviewRequired && !ocrReviewed && !isSuperAdmin) {
      return "bg-gray-200 cursor-not-allowed pointer-events-none opacity-50";
    } else if (invoiceHasFullConfidence && !selected) {
      return "bg-high-confidence-bg text-high-confidence-text";
    } else {
      return "";
    }
  };

  return (
    <div className="flex flex-col h-full gap-2">
      {!error ? (
        <div className="flex flex-1">
          <TableLayout ref={tableContainerRef}>
            {loading ? (
              <SkeletonTable
                columns={table._getColumnDefs()}
                columnVisibility={table.initialState.columnVisibility}
                tableContainerRef={tableContainerRef}
              />
            ) : (
              <Table ref={tableRef}>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead
                            key={header.id}
                            colSpan={header.colSpan}
                            style={{
                              width:
                                header.getSize() === Number.MAX_SAFE_INTEGER
                                  ? "auto"
                                  : header.getSize(),
                            }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody
                  loading={loading}
                  table={table}
                  emptyStateComponent={emptyStateComponent}
                >
                  <>
                    {table.getRowModel().rows?.length &&
                      table.getRowModel().rows.map((row) => (
                        <PrefetchPdfOnHover
                          key={row.id}
                          invoiceId={row.original.invoice.invoiceId}
                        >
                          <TableRow
                            key={row.id}
                            data-state={row.getIsSelected() && "selected"}
                            className={cn(
                              "my-1 h-[53px] group",
                              invoiceRowClass(
                                row.original.invoice,
                                row.getIsSelected(),
                              ),
                              row.original.invoice.status ===
                                InvoiceStatus.NEW &&
                                "animate-in fade-in duration-1000",
                            )}
                          >
                            <Row
                              row={row}
                              onRowClick={handleRowClick}
                              colSpan={table.getAllColumns().length}
                              clickableColumns={clickableColumns}
                            />
                          </TableRow>
                        </PrefetchPdfOnHover>
                      ))}
                  </>
                </TableBody>
              </Table>
            )}
            {children ?? null}
          </TableLayout>
        </div>
      ) : (
        <div className="flex flex-1 rounded-md border overflow-hidden">
          <ErrorMessage message={t("invoices-network-error")} />
        </div>
      )}
      <ServerSideDataTablePagination
        table={table}
        totalItems={totalItems}
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};

export const ProcessingStates = memo(
  ({ processingStatus }: { processingStatus: InvoiceProcessingStatus }) => {
    const [animate, setAnimate] = useState(false);
    const { t } = useTranslation();
    const states = [
      t("invoice_processing.extracting"),
      t("invoice_processing.classifying"),
      t("invoice_processing.accounting"),
    ];
    const index = InvoiceProcessingLoadingSteps.findIndex((steps) =>
      steps.includes(processingStatus),
    );
    const [previousIndex, setPreviousIndex] = useState(index);

    useEffect(() => {
      if (index !== previousIndex) {
        setAnimate(true);

        setTimeout(() => {
          setAnimate(false);
          setPreviousIndex(index);
        }, 1000);
      }
    }, [index, previousIndex]);

    return (
      <div className="relative h-9 overflow-hidden ">
        <div
          className={`absolute inset-0 flex items-center transform transition-transform duration-1000 ${
            animate ? "-translate-y-full" : "translate-y-0"
          }`}
          key={previousIndex}
        >
          <Clock size={16} className="mr-1 text-muted-foreground" />
          <p className="text-muted-foreground">{states[previousIndex]}</p>
        </div>
        <div
          className={`absolute inset-0 flex items-center transform transition-transform duration-1000 ${
            animate ? "translate-y-0" : "translate-y-full"
          }`}
          key={index}
        >
          <Clock size={16} className="mr-1 text-muted-foreground" />
          <p className="text-muted-foreground">{states[index]}</p>
        </div>
      </div>
    );
  },
);

type MemoizedCellProps = {
  cell: any;
  clickableColumns: string[];
  onRowClick: (event: any, rowData: any) => void;
  rowOriginal: any;
  duration: number;
};

const MemoizedCell = memo(
  ({
    cell,
    clickableColumns,
    onRowClick,
    rowOriginal,
    duration,
  }: MemoizedCellProps) => {
    const hasMeta = cell.getContext().cell.column.columnDef.meta;
    const cellContext = hasMeta?.getCellContext?.(cell.getContext());

    const isClickable = useMemo(
      () => clickableColumns.some((clickable) => cell.id.includes(clickable)),
      [clickableColumns, cell.id],
    );

    const handleClick = useCallback(
      (event: React.MouseEvent) => {
        if (isClickable) {
          onRowClick(event, rowOriginal);
        }
      },
      [isClickable, onRowClick, rowOriginal],
    );

    const cellWidth = cell.column.getSize();
    const width = cellWidth === Number.MAX_SAFE_INTEGER ? "auto" : cellWidth;

    const style = useMemo(
      () => ({
        ...(cellContext?.style ?? {}),
        width,
      }),
      [cellContext?.style, width],
    );

    return (
      <TableCell
        onClick={handleClick}
        style={style}
        align={cellContext?.align}
        className={cn("py-1 truncate-short")}
      >
        {cell.column.id === "select" ? (
          flexRender(cell.column.columnDef.cell, cell.getContext())
        ) : (
          <div
            className={cn(
              duration && "animate-in slide-in-from-bottom duration-500",
            )}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        )}
      </TableCell>
    );
  },
);

type RowProps = {
  row: TanstackRow<any>;
  colSpan: any;
  onRowClick: (event: any, rowData: any) => void;
  clickableColumns: string[];
};

const Row = memo(({ row, onRowClick, clickableColumns, colSpan }: RowProps) => {
  const prevStatus = usePreview(row.original.invoice.status);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (
      prevStatus === InvoiceStatus.NEW &&
      row.original.invoice.status !== InvoiceStatus.NEW
    ) {
      setDuration(ANIMATION_DURATION);
    }
  }, [prevStatus, row.original.invoice.status]);

  const cells = row.getVisibleCells();

  return (
    <>
      {cells.map((cell) => (
        <MemoizedCell
          key={cell.id}
          cell={cell}
          clickableColumns={clickableColumns}
          onRowClick={onRowClick}
          rowOriginal={row.original}
          duration={duration}
        />
      ))}
    </>
  );
});

const TableBody = ({
  loading,
  table,
  children,
  emptyStateComponent,
}: PropsWithChildren<{
  loading: boolean;
  table: any;
  emptyStateComponent?: React.ReactNode;
}>) => {
  if (table.getPreFilteredRowModel().rows.length === 0 && !loading) {
    const hasActiveFilters = table.hasActiveServerSideFilters?.() ?? false;

    if (!hasActiveFilters) {
      return (
        <TableBodyBase>
          <TableRow className="hover:bg-transparent hover:text-inherit">
            <TableCell colSpan={table.getAllColumns().length} className="h-96">
              {emptyStateComponent || <NoRecordsEmpty state="done" />}
            </TableCell>
          </TableRow>
        </TableBodyBase>
      );
    }

    return (
      <TableBodyBase>
        <TableRow className="hover:bg-transparent hover:text-inherit">
          <TableCell colSpan={table.getAllColumns().length} className="h-96">
            <NoRecordsEmpty
              state="no-filter-results"
              onReset={table.clearServerSideFilters}
            />
          </TableCell>
        </TableRow>
      </TableBodyBase>
    );
  }

  return (
    <TableBodyBase>
      {children}
      <div className="h-10" />
    </TableBodyBase>
  );
};
